import React from 'react';
import { Outlet } from 'react-router-dom';
import { AppShell, AnimatedPage, type NavSection } from '@theonefamily/ui';

const HubBar = React.lazy(() => import('hubBar/HubBar'));

import {
  LayoutDashboard, BarChart3,
  Link2, RefreshCw, ArrowRightLeft, Download, Layers, Upload,
  GitBranch, LayoutTemplate, Webhook, ArrowDownToLine, Monitor,
  Sparkles, MessageSquareText, Brain,
  Activity, AlertTriangle, MessagesSquare, GitMerge, ClipboardList,
  Braces, Wand2, Code2, Plug, FileJson,
  KeyRound, Building2, Store, Settings,
} from 'lucide-react';

const I = (icon: unknown) => icon as NavSection['items'][0]['icon'];

const NAVIGATION: NavSection[] = [
  {
    label: 'Overview',
    items: [
      { id: 'dashboard',  label: 'Dashboard', icon: I(LayoutDashboard), href: '/' },
      { id: 'analytics',  label: 'Analytics', icon: I(BarChart3),       href: '/analytics' },
    ],
  },
  {
    label: 'Data Operations',
    items: [
      { id: 'connections', label: 'Connections', icon: I(Link2),          href: '/connections' },
      { id: 'sync',        label: 'Sync',         icon: I(RefreshCw),      href: '/sync' },
      { id: 'import',      label: 'CSV Import',   icon: I(Upload),         href: '/import' },
      { id: 'migrations',  label: 'Migrations',   icon: I(ArrowRightLeft), href: '/migrations' },
      { id: 'export',      label: 'Export',        icon: I(Download),       href: '/export' },
      { id: 'bulk',        label: 'Bulk Ops',      icon: I(Layers),         href: '/bulk' },
    ],
  },
  {
    label: 'Automation',
    items: [
      { id: 'flows',            label: 'Flows',          icon: I(GitBranch),    href: '/flows' },
      { id: 'templates',        label: 'Templates',      icon: I(LayoutTemplate),href: '/templates' },
      { id: 'webhooks',         label: 'Webhooks',       icon: I(Webhook),      href: '/webhooks' },
      { id: 'inbound-webhooks', label: 'Inbound Hooks',  icon: I(ArrowDownToLine),href: '/inbound-webhooks' },
      { id: 'rpa',              label: 'RPA',            icon: I(Monitor),      href: '/rpa' },
    ],
  },
  {
    label: 'AI',
    items: [
      { id: 'ai-mapper',    label: 'AI Mapper',    icon: I(Sparkles),        href: '/ai-mapper' },
      { id: 'advisor',      label: 'AI Advisor',   icon: I(MessageSquareText),href: '/advisor' },
      { id: 'intelligence', label: 'Intelligence', icon: I(Brain),            href: '/intelligence' },
    ],
  },
  {
    label: 'Monitoring',
    items: [
      { id: 'monitoring',     label: 'Monitoring',   icon: I(Activity),      href: '/monitoring' },
      { id: 'conflicts',      label: 'Conflicts',    icon: I(GitMerge),      href: '/conflicts' },
      { id: 'errors',         label: 'Errors',       icon: I(AlertTriangle), href: '/errors' },
      { id: 'conversations',  label: 'Conversations',icon: I(MessagesSquare),href: '/conversations' },
    ],
  },
  {
    label: 'Developer',
    items: [
      { id: 'graphql',          label: 'GraphQL',         icon: I(Braces),   href: '/graphql' },
      { id: 'transforms',       label: 'Transforms',      icon: I(Wand2),    href: '/transforms' },
      { id: 'sdk',              label: 'SDKs',            icon: I(Code2),    href: '/sdk' },
      { id: 'custom-connectors',label: 'Custom Connectors',icon: I(Plug),   href: '/custom-connectors' },
      { id: 'api-docs',         label: 'API Docs',        icon: I(FileJson), href: '/api-docs' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { id: 'api-keys',        label: 'API Keys',      icon: I(KeyRound),  href: '/api-keys' },
      { id: 'company-api-keys',label: 'Company Keys',  icon: I(Building2), href: '/company-api-keys' },
      { id: 'marketplace',     label: 'Marketplace',   icon: I(Store),     href: '/marketplace' },
      { id: 'integrations',    label: 'Integrations',  icon: I(Plug),      href: '/integrations' },
      { id: 'audit',           label: 'Audit Log',     icon: I(ClipboardList), href: '/audit' },
      { id: 'settings',        label: 'Settings',      icon: I(Settings),  href: '/settings' },
    ],
  },
];

export default function AppLayout({ children }: { children?: React.ReactNode }) {
  return (
    <>
      <React.Suspense fallback={<div style={{ height: 48 }} />}>
        <HubBar
          currentProduct="bridge"
          apiBase={import.meta.env.VITE_BUS_URL ?? 'https://api.theonestack.com'}
          hubUrl={import.meta.env.VITE_HUB_URL}
          supportConfig={{
            apiBaseUrl: import.meta.env.VITE_OPS_CENTER_API_URL ?? 'https://api.theonestack.com',
            apiKey: import.meta.env.VITE_OPS_CENTER_API_KEY ?? '',
            platformId: 'bridge',
            platformName: 'The One Bridge',
          }}
        />
      </React.Suspense>
      <AppShell
        product={{ name: 'The One Bridge', accent: '#8b5cf6' }}
        navigation={NAVIGATION}
        user={{ name: 'Bridge Admin', email: 'admin@bridge.local', role: 'Administrator' }}
        onSignOut={() => { window.location.href = '/login'; }}
      >
        <AnimatedPage>{children ?? <Outlet />}</AnimatedPage>
      </AppShell>
    </>
  );
}
